import type { PatientSelectionItem } from "../../../Types/VoiceAssistant/VoiceAssistantTypes";

/**
 * EMR landing voice intents: open/select/choose (+ optional "patient" + name).
 */
const PATIENT_SELECTION_VERB = "(?:open|select|choose)";

export function isPatientSelectionPrompt(prompt: string): boolean {
  const trimmed = prompt.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();

  if (lower === "open" || lower === "select" || lower === "choose") return true;
  if (new RegExp(`^${PATIENT_SELECTION_VERB}(\\s+patient)?\\b`, "i").test(trimmed)) {
    return true;
  }
  if (new RegExp(`^${PATIENT_SELECTION_VERB}\\s+\\S`, "i").test(trimmed)) {
    return true;
  }
  if (new RegExp(`\\b${PATIENT_SELECTION_VERB}\\s+patient\\b`, "i").test(lower)) {
    return true;
  }

  return false;
}

export function normalizePatientSelectionData(
  data: unknown,
): PatientSelectionItem[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.filter(
      (item): item is PatientSelectionItem =>
        item !== null && typeof item === "object",
    );
  }
  if (typeof data === "object") {
    return [data as PatientSelectionItem];
  }
  return [];
}

/** EMR number for query string: `?emrno=${patientId}` from API `data[].patientId`. */
export function getEmrNoFromPatientSelectionItem(
  item: PatientSelectionItem,
): string {
  const fromEmr = item.emrNo ?? item.emrno ?? item.emrNoDisplay;
  if (fromEmr != null && String(fromEmr).trim()) {
    return String(fromEmr).trim();
  }
  const pid = item.patientId;
  if (typeof pid === "string" && pid.trim()) {
    return pid.trim();
  }
  return "";
}

export function getPatientMasterIdFromSelectionItem(
  item: PatientSelectionItem,
): number {
  const master = item.patientMasterId;
  if (master != null && Number.isFinite(Number(master))) {
    return Number(master);
  }
  if (typeof item.patientId === "number" && Number.isFinite(item.patientId)) {
    return item.patientId;
  }
  return 0;
}

export function getPatientSelectionNavigateTarget(item: PatientSelectionItem): {
  encounterId: number;
  patientMasterId: number;
  emrNo: string;
} | null {
  const encounterId = Number(item.encounterId ?? item.encounterID ?? 0);
  const patientMasterId = getPatientMasterIdFromSelectionItem(item);
  const emrNo = getEmrNoFromPatientSelectionItem(item);
  if (!encounterId || !patientMasterId) return null;
  return { encounterId, patientMasterId, emrNo };
}
